import { useState } from 'react';
import { ListItem, ListItemButton, Checkbox, Typography, Avatar, Alert, Snackbar, Chip, Box, useTheme, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PackingOperation, PackingTask } from '../schema';
import { getTrailersForDestination, type PackingListItemLike } from '../utils/taskDisplayHelpers';
import { updateTaskCompletion, type ItemLocationForLookup } from '../utils/taskCompletion';

interface Props {
    task: PackingTask;
    operation: PackingOperation;
    allItemLocations: ItemLocationForLookup[];
    /** When provided, enables trailer context for destination */
    packingLists?: Map<string, PackingListItemLike[]>;
}

export default function TaskRow({ task, operation, allItemLocations, packingLists }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const itemType = task.itemType;
    const fromEvent = task.fromEvent;
    const toEvent = task.toEvent;
    const fromStorageLocation = task.fromStorageLocation;
    const toStorageLocation = task.toStorageLocation;

    const isDisabled = operation.status === 'completed';
    const trailers = packingLists ? getTrailersForDestination(task, packingLists) : [];
    const trailerLabel = trailers.length > 0 ? ` (${trailers.map(t => t.name || 'Trailer').join(', ')})` : '';

    const formatSource = (): string => {
        if (fromEvent) return fromEvent.name ?? fromEvent.id;
        if (fromStorageLocation) return fromStorageLocation.name ?? 'Storage';
        return 'HQ';
    };

    const formatDestination = (): string => {
        if (toEvent) return toEvent.name ?? toEvent.id;
        if (toStorageLocation) return toStorageLocation.name ?? 'Storage';
        return 'HQ';
    };

    const handleCheckboxChange = async (checked: boolean) => {
        if (isDisabled || isUpdating) return;

        setIsUpdating(true);
        setError(null);

        try {
            await updateTaskCompletion(task, checked, allItemLocations);
        } catch (err: any) {
            console.error('Failed to update task:', err);
            setError(err.message || 'Failed to update task. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <>
            <ListItem
                disablePadding
                sx={{
                    opacity: task.completed ? 0.7 : 1,
                    bgcolor: task.completed ? 'action.hover' : 'transparent',
                }}
            >
                <ListItemButton
                    disabled={isDisabled || isUpdating}
                    onClick={() => handleCheckboxChange(!task.completed)}
                    sx={{
                        py: 1.5,
                        px: { xs: 0, sm: 2 },
                        display: 'block',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            gap: isMobile ? 0.5 : 2,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {/* Row 1: Checkbox, Avatar, Name, Quantity */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexGrow: 1,
                                minWidth: 0,
                                flexBasis: isMobile ? '100%' : 'auto',
                            }}
                        >
                            <Checkbox
                                checked={task.completed || false}
                                onChange={e => handleCheckboxChange(e.target.checked)}
                                disabled={isDisabled || isUpdating}
                                onClick={e => e.stopPropagation()}
                                sx={{ py: 0, pr: 0.5 }}
                            />
                            <Avatar src={itemType?.photoUrl} alt={itemType?.name} sx={{ width: 32, height: 32, mr: 1.5, flexShrink: 0 }}>
                                {itemType?.name?.[0]?.toUpperCase()}
                            </Avatar>
                            <Typography
                                variant="body1"
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                    fontWeight: 500,
                                }}
                            >
                                {itemType?.name || 'Unknown Item'}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, flexShrink: 0 }}>
                                {task.quantity} items
                            </Typography>
                        </Box>

                        {/* Row 2 (mobile) or inline (desktop): Source → Destination, Verify, Time */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                ...(isMobile ? { width: '100%', pt: 0.25, pl: 5 } : { mr: 1 }),
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                }}
                            >
                                {formatSource()}
                            </Typography>
                            <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                }}
                            >
                                {formatDestination()}
                                {trailerLabel}
                            </Typography>
                            {task.ambiguous && (
                                <Chip
                                    icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                                    label="Verify"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20 }}
                                />
                            )}
                            {task.completed && task.completedAt && (
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(task.completedAt).toLocaleTimeString()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </ListItemButton>
            </ListItem>

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
}
